import type { CSSProperties } from 'react';
import { ICONS } from '@ui/icons/types';
import { HvHostMap } from '@/connection/helpers';
import { BlockRendererContext } from './block-renderer';

export type SocialPlatform =
  | typeof ICONS.FACEBOOK
  | typeof ICONS.INSTAGRAM
  | typeof ICONS.TWITTER
  | typeof ICONS.LINKEDIN
  | typeof ICONS.YOUTUBE
  | typeof ICONS.TIKTOK;

export interface SocialLink {
  platform: SocialPlatform;
  href: string;
  /** hidden independently of href, so a platform can be configured but temporarily toggled off */
  hidden?: boolean;
}

export type SocialWidgetSize = 'small' | 'medium' | 'large';

export const SocialIcon = (social: SocialPlatform, color = '#000000') => {
  return `${HvHostMap.imageResizer}/generate-png/${social}.png?${new URLSearchParams({ color })}`;
};

const SIZE_PRESETS: Record<SocialWidgetSize, { container: number; iconSize: number }> = {
  small: { container: 32, iconSize: 14 },
  medium: { container: 40, iconSize: 18 },
  large: { container: 48, iconSize: 22 },
};

export interface SocialWidgetProps {
  links?: SocialLink[];
  size?: SocialWidgetSize;
  align?: 'left' | 'center' | 'right';
  /** icon glyph color - passed to the SocialIcon PNG-generation service; also used as the
   * border-color fallback below when no explicit borderColor is set */
  color?: string;
  background?: string;
  border?: string;
  borderStyle?: CSSProperties['borderStyle'];
  borderColor?: string;
  padding?: string;
  context?: BlockRendererContext;
}

export default function SocialWidget({
  links,
  size = 'medium',
  align = 'center',
  color,
  background,
  border,
  borderStyle,
  borderColor,
  padding = '8px 56px',
  context,
}: SocialWidgetProps) {
  // the settings panel always persists all platforms (for stable reordering), so unset/hidden
  // ones are filtered out here rather than at the panel/data-model level
  const visibleLinks = links?.filter((link) => link.href && !link.hidden) ?? [];
  if (!visibleLinks.length) return null;

  const { container, iconSize } = SIZE_PRESETS[size];
  const resolvedBorder = border
    ? `${border} ${borderStyle ?? 'solid'} ${borderColor ?? color ?? 'transparent'}`
    : undefined;

  return (
    <table role="presentation" width="100%" cellPadding={0} cellSpacing={0} border={0} style={{ background }}>
      <tbody>
        <tr>
          <td align={align} style={{ padding }}>
            <table cellPadding={0} cellSpacing={0} border={0} role="presentation">
              <tbody>
                <tr>
                  {visibleLinks.map((link) => (
                    <td key={link.platform} style={{ padding: '0 4px' }}>
                      <a href={link.href} style={{ display: 'block', textDecoration: 'none' }}>
                        <table cellPadding={0} cellSpacing={0} border={0} role="presentation">
                          <tbody>
                            <tr>
                              <td
                                align="center"
                                valign="middle"
                                width={container}
                                height={container}
                                style={{
                                  background,
                                  border: resolvedBorder,
                                  borderRadius: '9999px',
                                }}
                              >
                                <img
                                  src={SocialIcon(link.platform.toLowerCase(), color ?? context?.theme?.body)}
                                  width={iconSize}
                                  height={iconSize}
                                  alt=""
                                  style={{ display: 'block' }}
                                  data-platform={link.platform}
                                />
                              </td>
                            </tr>
                          </tbody>
                        </table>
                      </a>
                    </td>
                  ))}
                </tr>
              </tbody>
            </table>
          </td>
        </tr>
      </tbody>
    </table>
  );
}
